// Business-process canvas — the same ReactFlow stack as the Pipeline canvas
// (zoom / pan / fit / minimap-less, theme-cortex tokens), drawing a process
// definition graph: start / task / decision / end nodes linked by edges whose
// labels are decision outcomes. Purely presentational + graph-editing: all
// persistence goes through the parent (save_definition).
import React, { useCallback, useEffect, useMemo } from "react";
import ReactFlow, {
  Background,
  Controls,
  Handle,
  Position,
  ReactFlowProvider,
  useNodesState,
  useEdgesState,
  useReactFlow,
  MarkerType,
} from "reactflow";
import "reactflow/dist/style.css";
import "../../flow-studio/mount/theme-cortex.css";
import { CircleDot, Square, GitBranch, Flag, Bot, Users, UserRound, Mail, Upload, Check, Zap, Clock, Code2, Route, X } from "lucide-react";

export const WF_NODE_META = {
  start: { color: "#10b981", Icon: CircleDot, label: "Start" },
  task: { color: "#377AD6", Icon: Square, label: "Step" },
  decision: { color: "#f59e0b", Icon: GitBranch, label: "Decision" },
  end: { color: "#64748b", Icon: Flag, label: "End" },
};
export const wfEdgeId = (e) => e.id || (String(e.from) + ">" + String(e.to) + (e.outcome ? "#" + e.outcome : ""));

// Node card — the exact Pipeline-canvas recipe: color-mix of the type color
// into the dark node background, a 2px typed border, centered bold title,
// muted small subtitle, colored handles ringed with the node background.
// data.run (live-run mode): 'done' | 'active' | 'skipped' — done gets a green
// ring + check badge, active pulses (wf-pulse keyframes, scoped in the page),
// skipped dims.
// Side-effect chips derived from a completed task's recorded output — real
// events (database write-back, mail, calendar), not decoration.
function taskEffects(t) {
  if (!t) return [];
  let fd = t.form_data;
  if (typeof fd === "string") { try { fd = JSON.parse(fd); } catch { fd = {}; } }
  fd = fd || {};
  const fx = [];
  if (fd.db_written) fx.push("💾 written to database");
  if (fd.email_sent) fx.push("📧 email sent");
  if (fd.emulated || fd.event_id || fd.event_link) fx.push("📅 call scheduled");
  return fx;
}
function lastProgress(t) {
  if (!t) return null;
  let pr = t.progress;
  if (typeof pr === "string") { try { pr = JSON.parse(pr); } catch { pr = []; } }
  if (Array.isArray(pr) && pr.length) return String(pr[pr.length - 1].msg || "");
  return null;
}

function WfNode({ data, selected }) {
  const n = data.node;
  const meta = WF_NODE_META[n.type] || WF_NODE_META.task;
  const run = data.run;
  const t = data.runTask;
  const ring = run === "done" ? "0 0 0 2px #34d399" : selected ? "0 0 0 2px #6366f1, 0 0 14px rgba(99,102,241,.45)" : "none";
  const status = run === "active"
    ? (lastProgress(t) || (n.subprocess ? "running the sub-process…" : n.endpoint ? "running the endpoint…" : t && t.claimed_by ? "agent working…" : n.agent ? "waiting for an agent worker…" : "waiting for a person…"))
    : null;
  const effects = run === "done" ? taskEffects(t) : [];
  return (
    <div
      data-wf-node={n.id}
      data-wf-run={run || undefined}
      className={run === "active" ? "wf-pulse" : undefined}
      style={{
        background: `color-mix(in srgb, ${meta.color} 18%, var(--flow-node-bg))`,
        border: `2px solid ${meta.color}`,
        borderRadius: 8,
        padding: "8px 12px",
        fontSize: 12,
        minWidth: 160,
        maxWidth: 230,
        textAlign: "center",
        color: "var(--flow-text)",
        cursor: "pointer",
        opacity: run === "skipped" ? 0.4 : 1,
        boxShadow: ring,
        position: "relative",
      }}
    >
      {run === "done" ? (
        <span style={{ position: "absolute", top: -8, right: -8, background: "#059669", borderRadius: 999, width: 17, height: 17, display: "flex", alignItems: "center", justifyContent: "center", border: "2px solid var(--flow-node-bg)" }}>
          <Check size={10} color="#fff" strokeWidth={3.5} />
        </span>
      ) : null}
      <Handle type="target" position={Position.Left} id="in"
        style={{ background: meta.color, width: 10, height: 10, border: "2px solid var(--flow-node-bg)", visibility: n.type === "start" ? "hidden" : "visible" }} />
      {/* start keeps a hidden input port so trigger edges can still attach to
          legacy graphs that carry a Start node */}
      <div className="flex items-center justify-center gap-1.5">
        <meta.Icon size={13} style={{ color: meta.color }} />
        <span style={{ fontWeight: 600 }}>{n.title || n.id}</span>
      </div>
      <div style={{ fontSize: 9, color: "var(--flow-text-muted)", marginTop: 2, display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
        <span>{meta.label}{n.join === "all" ? " · join all" : ""}</span>
        {n.subprocess ? (
          <span style={{ color: "#f9a8d4", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 3 }}>
            · <Route size={9} /> {n.subprocess_name || "sub-process"}
          </span>
        ) : n.endpoint ? (
          <span style={{ color: "#67e8f9", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 3 }}>
            · <Code2 size={9} /> {n.endpoint}
          </span>
        ) : n.agent ? (
          <span style={{ color: "#b79df8", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 3 }}>
            · <Bot size={9} /> {n.agent}
          </span>
        ) : n.assignee ? (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>· <UserRound size={9} /> {n.assignee}</span>
        ) : n.role ? (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>· <Users size={9} /> {n.role}</span>
        ) : null}
      </div>
      {status ? (
        <div className="wf-status" data-wf-status
          style={{ fontSize: 9, marginTop: 4, color: "#93c5fd", fontWeight: 600, maxWidth: 200, marginLeft: "auto", marginRight: "auto" }}>
          {status}<span className="wf-dots" />
        </div>
      ) : null}
      {effects.length ? (
        <div style={{ marginTop: 4, display: "flex", flexDirection: "column", gap: 2, alignItems: "center" }}>
          {effects.map((e) => (
            <span key={e} className="wf-fx" data-wf-effect
              style={{ fontSize: 9, fontWeight: 700, color: "#67e8f9", background: "rgba(103,232,249,0.12)", border: "1px solid rgba(103,232,249,0.35)", borderRadius: 999, padding: "1px 8px" }}>
              {e}
            </span>
          ))}
        </div>
      ) : null}
      {n.type !== "end" && (
        <Handle type="source" position={Position.Right} id="out"
          style={{ background: meta.color, width: 10, height: 10, border: "2px solid var(--flow-node-bg)" }} />
      )}
    </div>
  );
}
// TRIGGER nodes — synthetic canvas nodes derived from the process's event
// listeners (never stored in the graph): the email trigger and the manual
// resume-upload FORM trigger. Clicking the upload trigger opens the run form.
// Trigger kinds render DATA-DRIVEN: known kinds get their icon/color, any
// new kind falls back to a generic bolt — no canvas change needed to add one.
// Label/hint come from the listener's config (form spec first).
const TRIGGER_KIND_META = {
  manual: { Icon: Upload, color: "#22d3ee" },
  email: { Icon: Mail, color: "#ec4899" },
  schedule: { Icon: Clock, color: "#f59e0b" },
  data_condition: { Icon: Zap, color: "#a3e635" },
};
function WfTriggerNode({ data }) {
  const l = data.listener;
  const cfg = l.config || {};
  const meta = TRIGGER_KIND_META[l.kind] || { Icon: Zap, color: "#a3e635" };
  const hasForm = l.kind === "manual" || !!cfg.form;
  const label = cfg.label || cfg.form?.title || cfg.form_title || cfg.display_label ||
    (l.kind === "email" ? "Email received"
      : l.kind === "schedule" ? "Schedule"
      : l.kind === "data_condition" ? "Data condition"
      : l.kind.charAt(0).toUpperCase() + l.kind.slice(1));
  const hint = l.kind === "schedule" ? `trigger · ${cfg.every || "scheduled"} · click to run`
    : l.kind === "data_condition" ? "trigger · fires on data · click to run"
    : hasForm ? "trigger · click to run"
    : l.kind === "email" ? `trigger · ${(cfg.query || "mailbox").slice(0, 34)}${(cfg.query || "").length > 34 ? "…" : ""}`
    : `trigger · ${l.kind} · click to run`;
  return (
    <div
      data-wf-trigger={l.kind}
      style={{
        background: `color-mix(in srgb, ${meta.color} 12%, var(--flow-node-bg))`,
        border: `2px dashed ${meta.color}`,
        borderRadius: 8,
        padding: "8px 12px",
        fontSize: 12,
        minWidth: 150,
        maxWidth: 210,
        textAlign: "center",
        color: "var(--flow-text)",
        cursor: "pointer",
        opacity: l.enabled === false ? 0.45 : 1,
      }}
    >
      <div className="flex items-center justify-center gap-1.5">
        <meta.Icon size={13} style={{ color: meta.color }} />
        <span style={{ fontWeight: 600 }}>{label}</span>
      </div>
      <div style={{ fontSize: 9, color: "var(--flow-text-muted)", marginTop: 2 }}>{hint}</div>
      <Handle type="source" position={Position.Right} id="out"
        style={{ background: meta.color, width: 10, height: 10, border: "2px solid var(--flow-node-bg)" }} />
    </div>
  );
}
const nodeTypes = { wf: WfNode, wfTrigger: WfTriggerNode };

// Entry nodes = where runs ENTER the graph and where trigger nodes attach:
// the Start node when the graph has one, otherwise the root steps (no
// incoming links) — mirrors the engine's entry rule exactly.
export function entryNodeIds(graph) {
  const nodes = graph.nodes || [];
  const edges = graph.edges || [];
  const start = nodes.find((n) => n.type === "start");
  if (start) return [start.id];
  return nodes.filter((n) => (n.type === "task" || n.type === "decision") && !edges.some((e) => e.to === n.id)).map((n) => n.id);
}

function buildNodes(graph, sel, runState, triggers) {
  const nodes = (graph.nodes || []).map((n) => ({
    id: n.id,
    type: "wf",
    position: { x: Number(n.x) || 0, y: Number(n.y) || 0 },
    data: { node: n, run: runState ? runState.nodes[n.id] : undefined, runTask: runState ? (runState.tasks || {})[n.id] : undefined },
    selected: !!(sel && sel.kind === "node" && sel.id === n.id),
  }));
  const entries = entryNodeIds(graph).map((id) => (graph.nodes || []).find((n) => n.id === id)).filter(Boolean);
  if (entries.length && triggers?.length) {
    const ex = Math.min(...entries.map((n) => Number(n.x) || 0));
    const ey = entries.reduce((s, n) => s + (Number(n.y) || 0), 0) / entries.length;
    triggers.forEach((l, i) => {
      nodes.push({
        id: "__trigger_" + l.id,
        type: "wfTrigger",
        position: { x: ex - 260, y: ey + (i - (triggers.length - 1) / 2) * 100 },
        data: { listener: l },
        draggable: false,
        selectable: false,
      });
    });
  }
  return nodes;
}
function buildEdges(graph, sel, runState, triggers) {
  const edges = (graph.edges || []).map((e) => {
    const id = wfEdgeId(e);
    const isSel = !!(sel && sel.kind === "edge" && sel.id === id);
    const fired = !!runState?.fired[id];
    const stroke = fired ? "#34d399" : isSel ? "#818cf8" : "#7c8ea6";
    return {
      id,
      source: e.from,
      target: e.to,
      label: e.outcome || undefined,
      animated: fired && runState?.running,
      labelStyle: { fontSize: 11, fontWeight: 700, fill: fired ? "#6ee7b7" : isSel ? "#a5b4fc" : "#fbbf24" },
      labelBgStyle: { fill: "var(--flow-canvas-bg)", fillOpacity: 0.85 },
      style: { stroke, strokeWidth: fired ? 2.4 : isSel ? 2.5 : 1.6 },
      markerEnd: { type: MarkerType.ArrowClosed, color: stroke, width: 16, height: 16 },
      selected: isSel,
    };
  });
  const entries = entryNodeIds(graph);
  if (entries.length && triggers?.length) {
    triggers.forEach((l) => {
      entries.forEach((target) => {
        edges.push({
          id: "__trigger_edge_" + l.id + "_" + target,
          source: "__trigger_" + l.id,
          target,
          targetHandle: "in",
          animated: !!runState?.running,
          style: { stroke: l.kind === "manual" ? "#22d3ee" : "#ec4899", strokeWidth: 1.6, strokeDasharray: "5 4", opacity: 0.8 },
          selectable: false,
        });
      });
    });
  }
  return edges;
}

function CanvasInner({ graph, onGraphChange, sel, onSelect, readOnly, runState, triggers, onTriggerClick }) {
  const [nodes, setNodes, onNodesChange] = useNodesState(buildNodes(graph, sel, runState, triggers));
  const [edges, setEdges] = useEdgesState(buildEdges(graph, sel, runState, triggers));
  useEffect(() => { setNodes(buildNodes(graph, sel, runState, triggers)); }, [graph, sel, runState, triggers, setNodes]);
  useEffect(() => { setEdges(buildEdges(graph, sel, runState, triggers)); }, [graph, sel, runState, triggers, setEdges]);
  // The listeners load AFTER mount, so the initial fitView doesn't know about
  // the trigger nodes placed left of the entry step — refit when they land or
  // they can end up outside the viewport (and unclickable).
  const rf = useReactFlow();
  const triggerCount = (triggers || []).length;
  useEffect(() => {
    if (!triggerCount) return;
    const t = setTimeout(() => { try { rf.fitView({ padding: 0.25, maxZoom: 1 }); } catch {} }, 80);
    return () => clearTimeout(t);
  }, [triggerCount, rf]);

  // Commit a drag to the authoring graph only when it ends — mid-drag motion
  // stays inside ReactFlow's own state.
  const onNodeDragStop = useCallback((_, node) => {
    onGraphChange({
      ...graph,
      nodes: graph.nodes.map((n) => (n.id === node.id ? { ...n, x: Math.round(node.position.x), y: Math.round(node.position.y) } : n)),
    });
  }, [graph, onGraphChange]);

  // New link. Decision sources auto-pick the first unused outcome (editable in
  // the link panel afterwards) — same behavior the engine validates.
  const onConnect = useCallback((c) => {
    if (readOnly || !c.source || !c.target || c.source === c.target) return;
    // synthetic trigger nodes never become graph edges — their wiring is
    // derived from the process's listeners
    if (String(c.source).startsWith("__trigger_") || String(c.target).startsWith("__trigger_")) return;
    const src = graph.nodes.find((n) => n.id === c.source);
    const ne = { from: c.source, to: c.target };
    if (src && src.type === "decision") {
      const used = graph.edges.filter((e) => e.from === src.id).map((e) => e.outcome);
      const oc = (src.outcomes || []).filter(Boolean);
      ne.outcome = oc.find((o) => !used.includes(o)) || oc[0] || "yes";
    }
    ne.id = wfEdgeId(ne);
    if (graph.edges.some((e) => wfEdgeId(e) === ne.id)) return;
    onGraphChange({ ...graph, edges: [...graph.edges, ne] });
    onSelect({ kind: "edge", id: ne.id });
  }, [graph, onGraphChange, onSelect, readOnly]);

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      nodeTypes={nodeTypes}
      onNodesChange={readOnly ? undefined : onNodesChange}
      onNodeDragStop={readOnly ? undefined : onNodeDragStop}
      onConnect={onConnect}
      onNodeClick={(_, node) => {
        if (node.type === "wfTrigger") { onTriggerClick?.(node.data.listener); return; }
        onSelect({ kind: "node", id: node.id });
      }}
      onEdgeClick={(_, edge) => { if (!String(edge.id).startsWith("__trigger_")) onSelect({ kind: "edge", id: edge.id }); }}
      onPaneClick={() => onSelect(null)}
      nodesDraggable={!readOnly}
      nodesConnectable={!readOnly}
      fitView
      fitViewOptions={{ padding: 0.25, maxZoom: 1 }}
      minZoom={0.2}
      maxZoom={2}
      proOptions={{ hideAttribution: true }}
      deleteKeyCode={null}
      style={{ backgroundColor: "var(--flow-canvas-bg)" }}
    >
      <Background color="var(--flow-dot-color)" gap={20} size={1} />
      <Controls showInteractive={false} position="bottom-left" />
    </ReactFlow>
  );
}

export default function WorkflowCanvas(props) {
  // Own provider so several canvases (editor + future run viewer) can coexist.
  const key = useMemo(() => props.canvasKey || "wf", [props.canvasKey]);
  return (
    <ReactFlowProvider key={key}>
      <CanvasInner {...props} />
    </ReactFlowProvider>
  );
}

// ── Composition map — the visualization for COMPOSABLE workflows ─────────────
// Definitions render as process cards; an edge A→B wherever a step of A runs
// B as a sub-process (node.subprocess = B's id or name, labeled with the step
// title). Read-only overlay; clicking a card opens that definition in the
// builder. Layout = BFS columns from the root processes (never referenced as
// a child), so composition reads left-to-right like a call graph.
function CompDefNode({ data }) {
  const d = data.def;
  const g = (() => { const v = typeof d.graph === "string" ? (() => { try { return JSON.parse(d.graph); } catch { return {}; } })() : (d.graph || {}); return v || {}; })();
  const nodes = Array.isArray(g.nodes) ? g.nodes : [];
  const steps = nodes.filter((n) => n.type === "task" || n.type === "decision").length;
  const agents = nodes.filter((n) => n.agent).length;
  const codes = nodes.filter((n) => n.endpoint).length;
  const subs = nodes.filter((n) => n.subprocess).length;
  return (
    <div onClick={() => data.onOpen?.(d.id)}
      style={{
        background: `color-mix(in srgb, ${data.current ? "#6366f1" : "#377AD6"} 16%, var(--flow-node-bg))`,
        border: `2px solid ${data.current ? "#6366f1" : "#377AD6"}`,
        borderRadius: 10, padding: "10px 14px", minWidth: 190, maxWidth: 250,
        color: "var(--flow-text)", cursor: "pointer", textAlign: "center",
      }}>
      <Handle type="target" position={Position.Left} id="in" style={{ background: "#f9a8d4", width: 9, height: 9, border: "2px solid var(--flow-node-bg)" }} />
      <div style={{ fontWeight: 700, fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
        <Route size={12} style={{ color: "#f9a8d4" }} /> {d.name}
      </div>
      <div style={{ fontSize: 9, color: "var(--flow-text-muted)", marginTop: 3 }}>
        {d.status === "published" ? "published" : "draft"} · {steps} steps
        {agents ? ` · ${agents} 🤖` : ""}{codes ? ` · ${codes} ⚡` : ""}{subs ? ` · ${subs} 🔁` : ""}
        {Number(d.running_count) ? ` · ${d.running_count} running` : ""}
      </div>
      <Handle type="source" position={Position.Right} id="out" style={{ background: "#f9a8d4", width: 9, height: 9, border: "2px solid var(--flow-node-bg)" }} />
    </div>
  );
}
const COMP_NODE_TYPES = { compDef: CompDefNode };

export function CompositionMap({ definitions, currentId, onOpen, onClose }) {
  const defs = definitions || [];
  const byId = {}; const byName = {};
  defs.forEach((d) => { byId[d.id] = d; byName[d.name] = d; });
  const refs = [];
  defs.forEach((d) => {
    let g = d.graph; if (typeof g === "string") { try { g = JSON.parse(g); } catch { g = {}; } }
    ((g && g.nodes) || []).forEach((n) => {
      if (!n.subprocess) return;
      const target = byId[n.subprocess] || byName[n.subprocess];
      if (target && target.id !== d.id) refs.push({ from: d.id, to: target.id, step: n.title || "" });
    });
  });
  // BFS columns from the roots (definitions nobody runs as a child).
  const referenced = new Set(refs.map((e) => e.to));
  const col = {}; const seen = new Set();
  let frontier = defs.filter((d) => !referenced.has(d.id)).map((d) => d.id);
  if (!frontier.length && defs.length) frontier = [defs[0].id];
  let depth = 0;
  while (frontier.length && depth < 12) {
    const next = [];
    frontier.forEach((id) => {
      if (seen.has(id)) return;
      seen.add(id); col[id] = depth;
      refs.forEach((e) => { if (e.from === id) next.push(e.to); });
    });
    frontier = next; depth += 1;
  }
  defs.forEach((d) => { if (!(d.id in col)) col[d.id] = depth; });
  const rowInCol = {};
  const rfNodes = defs.map((d) => {
    const c = col[d.id]; rowInCol[c] = rowInCol[c] || 0; const row = rowInCol[c]++;
    return { id: d.id, type: "compDef", position: { x: 60 + c * 320, y: 70 + row * 130 }, data: { def: d, current: d.id === currentId, onOpen }, draggable: false, connectable: false };
  });
  const rfEdges = refs.map((e, i) => ({
    id: "comp" + i, source: e.from, target: e.to, sourceHandle: "out", targetHandle: "in",
    label: e.step, animated: true,
    style: { stroke: "#f9a8d4", strokeWidth: 2 },
    labelStyle: { fill: "var(--flow-text-muted)", fontSize: 9 },
    labelBgStyle: { fill: "var(--flow-node-bg)", fillOpacity: 0.85 },
    markerEnd: { type: MarkerType.ArrowClosed, color: "#f9a8d4" },
  }));
  return (
    <div data-testid="wf-composition-map" style={{ position: "absolute", inset: 0, zIndex: 30, background: "var(--flow-bg, #0b1220)" }}>
      <div style={{ position: "absolute", top: 12, left: 14, zIndex: 31, fontSize: 12, fontWeight: 600, color: "var(--flow-text)", display: "flex", alignItems: "center", gap: 6 }}>
        <Route size={13} style={{ color: "#f9a8d4" }} /> Process composition
        <span style={{ fontWeight: 400, color: "var(--flow-text-muted)" }}>— which workflow runs which; click a process to open it</span>
      </div>
      <button onClick={onClose} aria-label="Close map"
        style={{ position: "absolute", top: 10, right: 12, zIndex: 31, background: "rgba(255,255,255,0.08)", border: "1px solid var(--flow-border,#334155)", color: "var(--flow-text)", borderRadius: 6, width: 26, height: 26, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
        <X size={14} />
      </button>
      {refs.length === 0 ? (
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--flow-text-muted)", fontSize: 13, zIndex: 30, pointerEvents: "none" }}>
          No sub-process links yet — add a "Sub-process" step to a workflow to compose processes.
        </div>
      ) : null}
      <ReactFlowProvider>
        <ReactFlow nodes={rfNodes} edges={rfEdges} nodeTypes={COMP_NODE_TYPES} fitView
          nodesDraggable={false} nodesConnectable={false} elementsSelectable={false} proOptions={{ hideAttribution: true }}>
          <Background gap={22} size={1} />
          <Controls showInteractive={false} />
        </ReactFlow>
      </ReactFlowProvider>
    </div>
  );
}
